import { Stack } from '@mui/material';
import { LayoutGroup } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { FeatureEnum } from '../../../../services/data/impl/cube/cubeSlice';
import { isBlueberryFeature } from '../../../../services/data/impl/cube/cubeUtils';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { useBlueberryFeatureIcon } from '../../../commonUI/BlueberryIcons';
import { ExpandingToggleButtonGroup } from '../../../commonUI/ExpandingToggleButtonGroup';
import { useBlueberryConfig } from '../../../dataMesh/blueberry/hooks/useBlueberryConfig';
import {
  selectCurrBlueberryClass,
  selectCurrFeature,
  setCurrBlueberryClass,
} from '../../../select/selectSlice';

type Props = {
  position: 'top-left' | 'bottom-left';
};

export const BlueberryFeatureFilter = ({ position = 'bottom-left' }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currBlueberryFeature = useAppSelector(selectCurrBlueberryClass);
  const currFeature = useAppSelector(selectCurrFeature);
  const blueberryConfig = useBlueberryConfig();
  const includeFlowers = blueberryConfig?.includeFlowers;
  const hasFlowerStages = blueberryConfig?.hasFlowerStages;
  const getIcon = useBlueberryFeatureIcon();

  const isBlueberry = useMemo(
    () => isBlueberryFeature(currFeature),
    [currFeature]
  );

  useEffect(() => {
    if (!isBlueberry) {
      dispatch(setCurrBlueberryClass(null));
    }
  }, [currFeature, dispatch, isBlueberry]);

  // If flowers are not included but flowers are selected from a previous scan, reset to the default view of total berries
  useEffect(() => {
    if (!includeFlowers && currBlueberryFeature === FeatureEnum.flower) {
      dispatch(setCurrBlueberryClass(null));
    }
  }, [currBlueberryFeature, includeFlowers, dispatch]);

  // need to add logic for blueberries + flowers, stages of flowers
  const handleFeatureSelection = (feature: FeatureEnum) => {
    if (
      feature === FeatureEnum.blueberry ||
      feature === FeatureEnum.blueberry_Blue ||
      feature === FeatureEnum.blueberry_Green ||
      feature === FeatureEnum.blueberry_Yellow ||
      feature === FeatureEnum.blueberry_Purple ||
      feature === FeatureEnum.flower ||
      feature === FeatureEnum.flower_closed ||
      feature === FeatureEnum.flower_open ||
      feature === FeatureEnum.flower_petalfall
    ) {
      dispatch(setCurrBlueberryClass(feature));
    }
  };

  const filteredFeature = currBlueberryFeature || 'all';

  const allButton = (
    <ExpandingToggleButtonGroup
      value={filteredFeature}
      name={t('Show All Flowers and Blueberries', { ns: 'ranch-view' })}
      icon={getIcon(null, 0.9)}
      mainButtonValue={'all'}
      handleClick={() => {
        // show all blueberries + flowers logic does not exist yet
        dispatch(setCurrBlueberryClass(null));
      }}
      nestedButtonProps={[]}
    />
  );

  const blueberryFilterButtons = (
    <ExpandingToggleButtonGroup
      value={filteredFeature}
      mainButtonValue={FeatureEnum.blueberry}
      icon={getIcon(FeatureEnum.blueberry)}
      name={t('Filter Blueberries', { ns: 'ranch-view' })}
      handleClick={(value) => handleFeatureSelection(value as FeatureEnum)}
      nestedButtonProps={[
        {
          value: FeatureEnum.blueberry_Blue,
          label: t('Blueberries - Blue', { ns: 'ranch-view' }),
          icon: getIcon(FeatureEnum.blueberry_Blue),
        },
        {
          value: FeatureEnum.blueberry_Purple,
          label: t('Blueberries - Purple', { ns: 'ranch-view' }),
          icon: getIcon(FeatureEnum.blueberry_Purple),
        },
        {
          value: FeatureEnum.blueberry_Yellow,
          label: t('Blueberries - Pink', { ns: 'ranch-view' }),
          icon: getIcon(FeatureEnum.blueberry_Yellow),
        },
        {
          value: FeatureEnum.blueberry_Green,
          label: t('Blueberries - Green', { ns: 'ranch-view' }),
          icon: getIcon(FeatureEnum.blueberry_Green),
        },
      ]}
    />
  );

  const flowerFilterButtons = (
    <ExpandingToggleButtonGroup
      value={filteredFeature}
      icon={getIcon(FeatureEnum.flower)}
      name={t('Filter Flowers', { ns: 'ranch-view' })}
      mainButtonValue={FeatureEnum.flower}
      handleClick={(value) => handleFeatureSelection(value as FeatureEnum)}
      nestedButtonProps={
        hasFlowerStages
          ? [
              {
                value: FeatureEnum.flower_petalfall,
                label: t('Flowers - Petal Fall', { ns: 'ranch-view' }),
                icon: getIcon(FeatureEnum.flower_petalfall),
              },
              {
                value: FeatureEnum.flower_open,
                icon: getIcon(FeatureEnum.flower_open),
                label: t('Flowers - Open', { ns: 'ranch-view' }),
              },
              {
                value: FeatureEnum.flower_closed,
                icon: getIcon(FeatureEnum.flower_closed),
                label: t('Flowers - Closed', { ns: 'ranch-view' }),
              },
            ]
          : []
      }
    />
  );

  const displayTopLeft = position === 'top-left';

  return (
    <Stack
      sx={{
        position: 'absolute',
        left: 0,
        zindex: 2,
        top: displayTopLeft ? '' : '0.8rem',
        bottom: displayTopLeft ? '2.5rem' : '',
        width: '4rem',
        alignItems: 'center',
      }}
      flexDirection={'column'}
      spacing={'.25rem'}
    >
      <LayoutGroup>
        {includeFlowers && allButton}
        {blueberryFilterButtons}
        {includeFlowers && flowerFilterButtons}
      </LayoutGroup>
    </Stack>
  );
};
